import { useTranslation } from 'react-i18next';
import { type FormEvent, useEffect, useId, useState } from 'react';

import { Button } from '@/components/shared/buttons';
import { FieldErrors } from '@/components/shared/form/FieldErrors';
import { TextInput } from '@/components/shared/form/inputs/TextInput';
import { Modal } from '@/components/shared/Modals/Modal';
import { Header } from '@/components/shared/typography/Header';
import { StyledLink } from '@/components/shared/StyledLink';
import { Body } from '@/components/shared/typography/Body';
import { IconBox } from '@/components/shared/IconBox';

import { SavedFiltersDefaultSwitch } from './SavedFiltersDefaultSwitch';
import { SavedFiltersModalHeader } from './SavedFiltersModalHeader';
import {
  SAVED_FILTER_NAME_MAX_LENGTH,
  type SavedFilterNameValidationError,
  validateSavedFilterName,
} from './validateSavedFilterName';
import style from './SavedFilters.module.sass';

type SavedFiltersSaveModalProps = {
  isOpen: boolean;
  isExistingName: (name: string) => boolean;
  hasFilters: boolean;
  duplicatePresetName?: string;
  onClose: () => void;
  onSave: (input: { name: string, asDefault: boolean, }) => void;
  onUseExistingPreset: (name: string) => void;
  renderSummary: (className?: string) => React.ReactNode;
};

export const SavedFiltersSaveModal = ({
  isOpen,
  hasFilters,
  isExistingName,
  duplicatePresetName,
  onClose,
  onSave,
  onUseExistingPreset,
  renderSummary,
}: SavedFiltersSaveModalProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const [presetName, setPresetName] = useState('');
  const [shouldSaveAsDefault, setShouldSaveAsDefault] = useState(false);
  const [shownNameError, setShownNameError] = useState<SavedFilterNameValidationError | null>(null);

  const normalizedName = presetName.trim();
  const isNameDuplicate = Boolean(normalizedName) && isExistingName(normalizedName);
  const nameValidationError = validateSavedFilterName({
    name: normalizedName,
    isDuplicate: isNameDuplicate,
  });

  useEffect(() => {
    if (!isOpen) return;

    setPresetName('');
    setShouldSaveAsDefault(false);
    setShownNameError(null);
  }, [isOpen]);

  const handleSave = () => {
    if (!hasFilters || !normalizedName) return;

    if (nameValidationError) {
      setShownNameError(nameValidationError);

      return;
    }

    onSave({ name: normalizedName, asDefault: shouldSaveAsDefault });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  return (
    <Modal
      isOpen={isOpen}
      handleCancel={onClose}
      variant='custom'
      className={style.savedFilterModal}>
      <SavedFiltersModalHeader
        title={t('savedFilters:saveModalTitle')}
        subtitle={t('savedFilters:saveModalSubtitle')}
        onClose={onClose}
      />
      <div className={style.modalBody}>
        <form id={formId} onSubmit={handleSubmit}>
          <label className={style.inputLabel}>
            <span className={style.fieldLabel}>{t('savedFilters:filterNameLabel')}</span>
            <TextInput
              value={presetName}
              placeholder={t('savedFilters:filterNamePlaceholder')}
              error={Boolean(shownNameError)}
              onChange={(event) => {
                setPresetName(event.currentTarget.value);
                setShownNameError(null);
              }}
              onClear={() => {
                setPresetName('');
                setShownNameError(null);
              }}
              sizeVariant='medium'
            />
            <div className={style.fieldErrorContainer}>
              {shownNameError ? (
                <FieldErrors>
                  {shownNameError === 'tooLong'
                    ? t('form:errors.tooLong', { count: SAVED_FILTER_NAME_MAX_LENGTH })
                    : t('savedFilters:duplicateNameError')}
                </FieldErrors>
              ) : null}
            </div>
          </label>
        </form>
        <div className={style.currentFilterPreview}>
          <Header variant='h5'>{t('savedFilters:currentFiltersHeading')}</Header>
          {renderSummary(style.modalFilterEntries)}
          {duplicatePresetName ? (
            <Body
              size='base'
              className={style.duplicatePresetAlert}>
              <IconBox
                iconName='Warning'
                wrapperClassName={style.duplicatePresetAlertIcon}
              />
              <span>
                {t('savedFilters:duplicateFiltersNotice', { name: duplicatePresetName })}{' '}
                <StyledLink
                  onClick={() => onUseExistingPreset(duplicatePresetName)}>
                  {t('savedFilters:useExistingPresetAction')}
                </StyledLink>
              </span>
            </Body>
          ) : null}
        </div>
        <SavedFiltersDefaultSwitch
          value={shouldSaveAsDefault}
          onChange={setShouldSaveAsDefault}
        />
      </div>
      <Modal.Footer>
        <Modal.CancelButton
          sizeVariant='small'
          onClick={onClose}
        />
        <div className={style.footerButton}>
          <Button
            width='full'
            sizeVariant='small'
            disabled={!hasFilters || !normalizedName}
            type='submit'
            form={formId}>
            {t('common:save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
